import { KeyAndScale, StemType } from '@arpeggi/shared'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import { Control, useWatch } from 'react-hook-form'
import { DAW_COLORS, styled } from '../../lib/stitches'
import { ISongStore } from '../../stores/song/SongStore'
import { IStudioStore } from '../../stores/StudioStore'
import { Row } from '../core/Components'
import { MediaTypes } from '../core/Utils'
import { gray1, gray15 } from '../platform/color'
import { UploadSoundModalForm } from '../studio/mint/upload-sound-modal/MintSoundUploadPage'
import Badge from './common/Badge'
import HeaderContent from './common/HeaderContent'
import VisualizerContainer from './common/VisualizerContainer'
import VisualizerWithOverlay, { MockVisualizerWithOverlay } from './common/VisualizerWithOverlay'
import PlayerError from './PlayerError'
import {
  ArtistData,
  StudioSongPlayerProps,
  useDraftPlayer,
  usePlayerParamsResolver,
  usePreviewSongPlayer,
  useSamplePlayer,
  useSongFromMintStatusPlayer,
  useSongPlayer,
  useStemPlayer,
  useUploadSoundPlayerData
} from './PlayerUtils'
import SamplePlaybackControls from './sample/SamplePlaybackControls'
import SampleTimelineControls from './sample/SampleTimelineControls'
import SampleVisualizer from './sample/SampleVisualizer'
import { SampleSkeletonPlayer } from './Skeleton'
import SongVisualizer from './song/NewSongVisualizer'
import SongPlaybackControls from './song/SongPlaybackControls'
import SongTimelineControls from './song/SongTimelineControls'

const StyledPlayerContainer = styled('div', {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'space-between',
  overflow: 'hidden',
  background: gray1,
  fontFamily: 'DM Sans',
  color: gray15
})

const StyledPlayerHeader = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '16px 10px 0px 10px'
})

const StyledPlayerBody = styled('div', {
  padding: '10px',
  flex: '1',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  overflow: 'hidden',
  minHeight: '0',
  userSelect: 'none'
})

const StyledPlayerFooter = styled('div', {
  padding: '0px 10px 12px 10px'
})

export const PlayerContainer = StyledPlayerContainer
export const PlayerHeader = StyledPlayerHeader
export const PlayerBody = StyledPlayerBody
export const PlayerFooter = StyledPlayerFooter

const Error = () => (
  <PlayerContainer>
    <PlayerError />
  </PlayerContainer>
)

interface SomePlayerProps {
  originToken: number
  token?: number // this is the ARP Media Index (the 'index' column in any of the media tables)
  autoPlay?: boolean
}

// SONG + STEMS: They use the same player. //

export const TokenSongPlayer: FC<SomePlayerProps> = observer(
  ({ originToken, token, autoPlay = false }) => {
    return TokenSongOrStemPlayer({ token, originToken, autoPlay }, true)
  }
)

export const StemPlayer: FC<{ originToken: number; token?: number; autoPlay: boolean }> = ({
  originToken,
  token,
  autoPlay = false
}) => TokenSongOrStemPlayer({ token, originToken, autoPlay }, false)

export const TokenSongOrStemPlayer: FC<SomePlayerProps> = (
  { token, originToken, autoPlay = false },
  isSong: boolean
) => {
  const { status, data, type, mediaHasComposition, mediaNotFound } = isSong
    ? useSongPlayer(originToken, token, autoPlay)
    : useStemPlayer(originToken, token, autoPlay)

  if (mediaNotFound) {
    if (isSong) {
      return <SongFromMintStatusPlayer originTokenId={Number(originToken)} />
    } else {
      console.warn('No arpMedia found for stem with token', originToken)
      return null
    }
  }

  // If media doesn't have composition or is an audio stem, fall back to sample player
  if (
    !mediaHasComposition ||
    (!isSong && data?.song?.stems.length === 1 && data.song.stems[0].type() === StemType.AUDIO)
  ) {
    console.log(
      'Stem or Song has no composition or is an audio stem, falling back to sample player'
    )
    return <SamplePlayer originToken={originToken} token={token} autoPlay={autoPlay} />
  }

  if (status === 'loading') return <SampleSkeletonPlayer />
  if (status === 'error') return <Error />

  const { player, buffer, ...props } = data
  if (type === 'wav') {
    return <WavSongPlayer player={player as HTMLAudioElement} buffer={buffer} {...props} />
  } else {
    return <SongPlayer player={player as IStudioStore} {...props} status={status} />
  }
}

// SAMPLE SPECIFIC //

export const SamplePlayer: FC<SomePlayerProps & { autoPlay?: boolean }> = observer(
  ({ token, originToken, autoPlay = false }) => {
    const data = useSamplePlayer(originToken, autoPlay, token)
    const { status, audioBuffer, artist, title, instrument, audio, timesUsed, tempo, key } = data

    if (status === 'loading') return <SampleSkeletonPlayer />
    if (status === 'error') return <Error />

    return (
      <PlayerContainer>
        <PlayerHeader>
          <HeaderContent title={title} artist={artist} />
        </PlayerHeader>
        <PlayerBody>
          <SampleVisualizer
            audio={audio}
            audioBuffer={audioBuffer}
            instrument={instrument}
            tempo={tempo}
            timesUsed={timesUsed}
            originalKey={key}
          />
        </PlayerBody>
        <SamplePlayerFooter audio={audio} />
      </PlayerContainer>
    )
  }
)

export const UploadSoundPlayer = observer(
  ({
    audioFile,
    dataUri,
    instrument,
    genres,
    control
  }: {
    audioFile: File
    dataUri: string
    instrument: string
    genres: string[]
    control: Control<UploadSoundModalForm>
  }) => {
    const data = useUploadSoundPlayerData({
      audioFile,
      dataUri,
      genres
    })
    const title = useWatch({ control, name: 'sampleTitle' })
    const key = useWatch({ control, name: 'key' })

    const keyAndScale = new KeyAndScale()
    keyAndScale.fromInt(key)

    if (data.status === 'loading') return <SampleSkeletonPlayer />
    if (data.status === 'error') return <Error />

    return (
      <PlayerContainer>
        <PlayerHeader>
          <HeaderContent title={title} artist={data.artist} />
        </PlayerHeader>
        <PlayerBody>
          <SampleVisualizer
            audio={data.audio}
            audioBuffer={data.audioBuffer}
            instrument={instrument}
            originalKey={keyAndScale.displayName()}
          />
        </PlayerBody>
        <SamplePlayerFooter audio={data.audio} />
      </PlayerContainer>
    )
  }
)

const SamplePlayerFooter = ({ audio }: { audio: HTMLAudioElement }) => (
  <PlayerFooter>
    <SampleTimelineControls audio={audio} />
    <SamplePlaybackControls audio={audio} />
  </PlayerFooter>
)

export const SongPlayer: FC<StudioSongPlayerProps> = observer((props) => {
  const { status, artist, title, player, song, bpm, stem } = props
  const color = song.tokenColor || DAW_COLORS.indigo03

  if (status === 'loading') return <SampleSkeletonPlayer />
  if (status === 'error') return <Error />

  return (
    <PlayerContainer>
      <PlayerHeader>
        <HeaderContent artist={artist} title={title} color={song.tokenColor} />
      </PlayerHeader>
      <PlayerBody>
        <VisualizerContainer>
          <Row css={{ marginBottom: '4px', justifyContent: 'space-between' }}>
            <Badge>{stem ? 'Stem' : 'Song'}</Badge>
            <Badge>{`BPM: ${bpm}`}</Badge>
          </Row>
          <MockVisualizerWithOverlay
            player={player}
            css={{ height: '50px', minHeight: '50px', maxHeight: '50px' }}
            seed={`${bpm}-${artist}`}
            options={{ color, resolution: 4, segmentSpacing: 1, segmentWidth: 1.5 }}
          />
          <SongVisualizer
            song={song}
            css={{ marginTop: '-50px', paddingTop: '60px', zIndex: 5 }}
            player={player}
            color={color}
          />
        </VisualizerContainer>
      </PlayerBody>
      <PlayerFooter>
        <SongTimelineControls player={player} />
        <SongPlaybackControls player={player} />
      </PlayerFooter>
    </PlayerContainer>
  )
})

export const PreviewSongPlayer: FC = observer(() => {
  const StudioSongPlayerProps = usePreviewSongPlayer()
  return <SongPlayer {...StudioSongPlayerProps} />
})

interface WavStudioSongPlayerProps {
  title: string
  artist: ArtistData
  bpm: number
  player: HTMLAudioElement
  buffer: AudioBuffer
  song: ISongStore
}

const WavSongPlayer: FC<WavStudioSongPlayerProps> = observer((props) => {
  const { song, player, buffer, artist, title, bpm } = props
  const color = song.tokenColor || DAW_COLORS.indigo03
  return (
    <PlayerContainer>
      <PlayerHeader>
        <HeaderContent artist={artist} title={title} color={song.tokenColor} />
      </PlayerHeader>
      <PlayerBody>
        <VisualizerContainer>
          <Row css={{ marginBottom: '4px', justifyContent: 'space-between' }}>
            <Badge>{`Song`}</Badge>
            <Badge>{`BPM: ${bpm}`}</Badge>
          </Row>
          <VisualizerWithOverlay
            css={{ height: '50px', minHeight: '50px', maxHeight: '50px' }}
            audio={player}
            audioBuffer={buffer}
            options={{ color, resolution: 4, segmentSpacing: 1, segmentWidth: 1.5 }}
          />
          <SongVisualizer
            song={song}
            css={{ marginTop: '-50px', paddingTop: '60px', zIndex: 5 }}
            player={player}
            color={color}
          />
        </VisualizerContainer>
      </PlayerBody>
      <PlayerFooter>
        <SampleTimelineControls audio={player} />
        <SamplePlaybackControls audio={player} />
      </PlayerFooter>
    </PlayerContainer>
  )
})

interface DraftPlayerProps {
  draftCode: string
}
const DraftPlayer: FC<DraftPlayerProps> = observer(({ draftCode }) => {
  const props = useDraftPlayer(draftCode)
  return <SongPlayer {...props} />
})

interface SongPreviewPlayerProps {
  originTokenId: number
}

const SongFromMintStatusPlayer: FC<SongPreviewPlayerProps> = ({ originTokenId }) => {
  const { player, playerStatus, user, mintStatus } = useSongFromMintStatusPlayer(originTokenId)

  return (
    <SongPlayer
      artist={{
        avatar: user?.profilePicture?.url,
        name: user?.displayName,
        url: `${window.location.origin}/profile/${user?.walletAddress}`
      }}
      player={player}
      status={playerStatus}
      title={mintStatus?.metadata.title}
      song={player.song}
      bpm={mintStatus?.metadata.bpm}
    />
  )
}

export const Player: FC = observer(() => {
  const { type, originToken, draftCode } = usePlayerParamsResolver()
  if (type === MediaTypes.SAMPLE) {
    return <SamplePlayer originToken={originToken} />
  }
  if (type === MediaTypes.SONG) {
    return <TokenSongPlayer originToken={originToken} />
  }
  if (type === MediaTypes.STEM) {
    return <StemPlayer originToken={originToken} autoPlay={false} />
  }
  if (type === 'draft') {
    return <DraftPlayer draftCode={draftCode} />
  } else {
    return <Error />
  }
})

export default Player
